import * as React from "react";
import { logger } from "@origin-digital/reporting-client";
import {
  dapiFetch,
  dapiFetchCache,
  IDapiCacheRequest,
  IErrorFetchResponse,
  xhrFetch,
  xhrFetchCache,
} from "@origin-digital/daxi";

export interface IFetch<T> {
  data?: T;
  error?: boolean;
  loading: boolean;
}

export interface IFetchOptions<T, M>
  extends Omit<IDapiCacheRequest, "ttlInMin"> {
  xhr?: boolean; // true for xhr request, otherwise dapi
  transform?: (data: M) => T;
  shouldExecute?: boolean; // deps - delays the execution of this hook, can we used to wait for other data load first
  noLogError?: boolean; // suppresses error message on failure
  ttlInMin?: IDapiCacheRequest["ttlInMin"]; // optional, if set uses cache
}
/**
 * React hook that simplifies xhr / dapi requests, returns an object of
 *   - {loading:boolean, error:boolean, data:T}
 *
 *   shouldExecute - default true, allows users to delay exec of fetch, since you can't but an if statement before this hook runs
 *   deps - default is [url + options.params + shouldExecute], can be overridden
 */
export const useDaxi = <T, M = T>(
  url: string,
  config?: IFetchOptions<T, M>,
  deps?: React.DependencyList
): IFetch<T> => {
  const [fetchObj, setFetchObj] = React.useState<IFetch<T>>({
    loading: false,
  });

  const { shouldExecute = true, xhr, noLogError, ...options } = config || {};

  const executeRequest = () => {
    setFetchObj({ loading: true });
    const daxi = xhr
      ? options?.ttlInMin // if the ttl is set use cache
        ? xhrFetchCache
        : xhrFetch
      : options?.ttlInMin
      ? dapiFetchCache
      : dapiFetch;
    (daxi as typeof dapiFetch)<M>(url, options)
      .then((res) => {
        const transformedData =
          options.transform && res.data
            ? options?.transform(res.data)
            : res.data;
        setFetchObj({
          error: false,
          data: (transformedData as unknown) as T,
          loading: false,
        });
      })
      .catch((err: IErrorFetchResponse<Record<string, unknown>>) => {
        if (!noLogError) {
          logger.error(`[useDaxi] error url: "${url}"`, err);
        }
        setFetchObj({
          error: true,
          loading: false,
        });
      });
  };

  // ensures that deps trigger when options.params or URL changes
  const depsUrl = JSON.stringify({ url, ...options.params });

  React.useEffect(() => {
    if (options.method && options.method.toLowerCase() !== "get") {
      logger.error(`[useDaxi] supports get requests only`, { ...options, url });
      setFetchObj({ error: true, loading: false });
    } else if (shouldExecute) {
      executeRequest();
    }
  }, deps ?? [depsUrl, shouldExecute]);
  return fetchObj;
};
